import React, { useEffect, useState } from 'react';
import styles from './register.module.scss';
import { demoDayBulb } from 'src/assets/images';
import { Button } from 'src/components/common';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { clearDemoDay, paymentForDemoDay, paymentVerification, registerDemoDay } from 'src/store/venture/venture.slice';
import { loadRazorpayScript } from 'src/utils/loadRazorPayScript/loadRazorPayScript';
import { DemoDayBooking, PaymentOrderResponse, RazorpayOptions, RazorpayResponse, VerifyPaymentResponse } from 'src/store/venture/venture.types';
import { openPopup } from 'src/store/common/common.slice';
import { useGetIdentity, useUrlParamValue } from 'src/hooks';
import { VentureAPIService } from 'src/services/venture.service';

export default function Register() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { paramValue } = useUrlParamValue();
    const { getIdentities } = useGetIdentity();
    const { demoDay } = useAppSelector(
        (store) => store.venture
    );
    const { myCompany } = useAppSelector((store) => store.company);
    const profile = useAppSelector((store) => store.profile.profile);
    const { loggedInUserType, authenticatedId } = getIdentities();
    const name: string = loggedInUserType === 'User' ? profile?.name : myCompany?.name;
    const email: string = loggedInUserType === 'User' ? profile?.email : myCompany?.email;

    const ventureVerseId: string = paramValue({ paramName: 'id' }) as string;
    const [loading, setLoading] = useState<boolean>(false)

    function handleBack() {
        navigate('/demo-day', { replace: true });
    }

    async function verifyPayment(paymentResponse: RazorpayResponse, extraData: any) {
        // eslint-disable-next-line camelcase
        const { razorpay_order_id, razorpay_payment_id, razorpay_signature } = paymentResponse;

        const payload = {
            // eslint-disable-next-line camelcase
            razorpay_order_id,
            // eslint-disable-next-line camelcase
            razorpay_payment_id,
            // eslint-disable-next-line camelcase
            razorpay_signature
        };

        dispatch(paymentVerification(payload))
            .then((response) => {
                const payload: DemoDayBooking = {
                    ventureVerseId,
                    paymentStatus: 'completed',
                    paymentDetails: {
                        ...extraData,
                        // eslint-disable-next-line camelcase
                        razorpay_order_id,
                        // eslint-disable-next-line camelcase
                        razorpay_payment_id,
                        // eslint-disable-next-line camelcase
                        razorpay_signature

                    },
                    demoDayProgress: 'Register',
                    userId: authenticatedId
                }
                dispatch(registerDemoDay(payload)).then((response) => {
                    if (response?.data?.status) {
                        dispatch(
                            openPopup({
                                popupMessage: 'Thanks for your payment! Just a moment while we finish your registration.',
                                popupType: 'success'
                            })
                        );
                        setLoading(false);
                        dispatch(clearDemoDay())
                        navigate(`/demo-day-register/document-submission?id=${ventureVerseId}`, { replace: true })
                    }
                }).finally(() => {
                    setLoading(false)
                })
            })
            .catch((error) => {
                console.error('Error verifying payment:', error);
                dispatch(
                    openPopup({
                        popupMessage: 'Something went wrong',
                        popupType: 'error'
                    })
                );
                setLoading(false)
            })
    }

    async function checkIfNotAlreadyRegister() {
        const demoDayPayload = { ventureVerseId }
        try {
            const response: any =
                await new VentureAPIService().getDemoDay(demoDayPayload);
            if (response.status === 200) {
                const demoDayResponse = response?.data?.data
                if (!demoDayResponse) return { state: true, navigate: '' }
                const button = demoDayResponse?.demoDayProgress === 'Register' ? 1 : demoDayResponse?.demoDayProgress === 'Pitch Deck' ? 2 : demoDayResponse?.demoDayProgress === 'Done' ? 3 : 0
                const navigatePath = button === 1 ? `/demo-day-register/document-submission?id=${ventureVerseId}` : button === 2 ? `/demo-day-register/slot-booking?id=${ventureVerseId}` : `/demo-day-detail?id=${ventureVerseId}`
                return { state: false, navigate: navigatePath }
            } else {
                dispatch(
                    openPopup({
                        popupMessage: 'Something went wrong',
                        popupType: 'error'
                    })
                );
                return { state: false, navigate: '' }
            }
        } catch (err) {
            if (!err.response?.data?.status || err.response?.status === 404) {
                return { state: true, navigate: '' }
            }
            dispatch(
                openPopup({
                    popupMessage: 'Something went wrong. Please refresh',
                    popupType: 'error'
                })
            );
            return { state: false, navigate: '' }
        }
    }

    async function handlePayment() {
        setLoading(true);
        const registrationChecking = await checkIfNotAlreadyRegister()
        const isNoRegister = registrationChecking?.state
        const navigateTo = registrationChecking?.navigate
        if (isNoRegister) {
            const payload = {
                amount: 249,
                currency: 'INR',
                ventureVerseId,
                userId: authenticatedId
            };

            dispatch(paymentForDemoDay(payload))
                .then(async (response: PaymentOrderResponse) => {
                    // eslint-disable-next-line camelcase                
                    const { key_id, order_id, amount, receipt } = response.data?.data;
                    const options: RazorpayOptions = {
                        // eslint-disable-next-line camelcase
                        key: key_id,
                        amount,
                        currency: 'INR',
                        name: 'Payment for Demo Day Participation',
                        description: 'Join Htraction Demo Day',
                        // eslint-disable-next-line camelcase
                        order_id: order_id,
                        handler: function (response: RazorpayResponse) {
                            verifyPayment(response, {
                                // eslint-disable-next-line camelcase
                                key_id,
                                // eslint-disable-next-line camelcase
                                order_id,
                                amount,
                                receipt
                            });
                        },
                        modal: {
                            ondismiss: () => {
                                console.log('Payment popup closed by user');
                                setLoading(false);
                            }
                        },
                        prefill: {
                            name,
                            email,
                            contact: ''
                        },
                        theme: {
                            color: '#fa4616'
                        }
                    };

                    const razorpay = new window.Razorpay(options);
                    razorpay.open();
                }).catch((err) => {
                    console.log(err);
                    dispatch(
                        openPopup({
                            popupMessage: 'Something went wrong',
                            popupType: 'error'
                        })
                    );
                    setLoading(false)
                })
        } else {
            setLoading(false)
            if (navigateTo) {
                navigate(navigateTo)
            }
        }
    }
    useEffect(() => {
        loadRazorpayScript();
    }, []);
    useEffect(() => {
        if (demoDay && demoDay !== 'undefined') {
            const button = demoDay?.demoDayProgress === 'Register' ? 1 : demoDay?.demoDayProgress === 'Pitch Deck' ? 2 : demoDay?.demoDayProgress === 'Done' ? 3 : 0
                const navigatePath = button === 1 ? `/demo-day-register/document-submission?id=${ventureVerseId}` : button === 2 ? `/demo-day-register/slot-booking?id=${ventureVerseId}` : `/demo-day-detail?id=${ventureVerseId}`
            navigate(navigatePath)
        }
    }, [demoDay])

    return (
        <div className={styles.register}>
            <h2>Secure Your Spot</h2>
            <p>
                Join Htraction Demo Day & take your startup from idea to impact. <br />
                This is your shot to connect, pitch, and rise.
            </p>
            <div className={styles.benefits}>
                <h4>What You Get</h4>
                <ul>
                    <li>
                        <img src={demoDayBulb} className={styles.listStyle} alt="bulb" />{' '}
                        <span>Access to Final Demo Day</span>
                    </li>
                    <li>
                        <img src={demoDayBulb} className={styles.listStyle} alt="bulb" />{' '}
                        <span>Network with visionary founders & top investors</span>
                    </li>
                    <li>
                        <img src={demoDayBulb} className={styles.listStyle} alt="bulb" />{' '}
                        <span>Chance to pitch your idea (for selected startups only)</span>
                    </li>
                    <li>
                        <img src={demoDayBulb} className={styles.listStyle} alt="bulb" />{' '}
                        <span>Exclusive exposure to industry experts</span>
                    </li>
                </ul>
            </div>
            <div className={styles.ctaButtons}>
                <Button
                    variant="secondary"
                    size="small"
                    style={{ width: 'auto', height: '50px', borderRadius: '47px' }}
                    onClick={handleBack}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="small"
                    style={{ minWidth: '180px', width: 'auto', height: '50px', borderRadius: '47px' }}
                    onClick={handlePayment}
                    loading={loading}
                    disabled={loading}
                >
                    Register & Pay ₹249
                </Button>
            </div>
        </div>
    );
}
